import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useIngredientCategories } from '../hooks/useIngredientCategories';
import { useUnits } from '../hooks/useUnits';
import type { Ingredient, IngredientCategory, IngredientUnit, ListDestination } from '@/types/ingredient.types';

interface IngredientModalProps {
  isOpen: boolean;
  ingredient: Ingredient | null;
  onClose: () => void;
  onSave: (data: Partial<Ingredient>) => void;
}

export const IngredientModal: React.FC<IngredientModalProps> = ({
  isOpen,
  ingredient,
  onClose,
  onSave
}) => {
  const { categoryOptions, loading: categoriesLoading } = useIngredientCategories();
  const { unitOptions, loading: unitsLoading } = useUnits();
  const [formData, setFormData] = useState<Partial<Ingredient>>({
    name: '',
    description: '',
    category: 'otros' as IngredientCategory,
    unit: 'unidad' as IngredientUnit,
    listDestination: 'AMBOS',
    dispatchMultiple: 1,
    purchaseDestination: 'AMBOS',
    trackInventory: true,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Las unidades ahora se cargan desde la API mediante useUnits

  useEffect(() => {
    if (ingredient) {
      setFormData({
        name: ingredient.name,
        description: ingredient.description,
        // Use categoryName if available (from API), otherwise fall back to category
        category: (ingredient as any).categoryName || ingredient.category,
        // Use unitName if available (from API), otherwise fall back to unit
        unit: (ingredient as any).unitName || ingredient.unit,
        listDestination: ingredient.listDestination ?? 'AMBOS',
        dispatchMultiple: ingredient.dispatchMultiple ?? 0,
        purchaseDestination: ingredient.purchaseDestination ?? 'AMBOS',
        trackInventory: ingredient.trackInventory ?? true,
        isActive: ingredient.isActive
      });

    } else {
      setFormData({
        name: '',
        description: '',
        category: 'otros' as IngredientCategory,
        unit: 'unidad' as IngredientUnit,
        listDestination: 'AMBOS',
        dispatchMultiple: 1,
        purchaseDestination: 'AMBOS',
        trackInventory: true,
        isActive: true
      });
    }
  }, [ingredient]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validar nombre (obligatorio)
    if (!formData.name?.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }

    // Validar categoría (obligatorio)
    if (!formData.category) {
      newErrors.category = 'La categoría es obligatoria';
    }

    // Validar unidad (obligatorio)
    if (!formData.unit) {
      newErrors.unit = 'La unidad es obligatoria';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Para nuevos ingredientes, no crear locationStocks inicialmente
      // El stock se manejará por separado a través de ajustes de inventario
      const ingredientData = {
        ...formData,
        // Mantener locationStocks existentes para edición, undefined para nuevos
        locationStocks: ingredient ? ingredient.locationStocks : undefined
      };

      await onSave(ingredientData);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      category: 'otros' as IngredientCategory,
      unit: 'unidad' as IngredientUnit,
      listDestination: 'AMBOS',
      dispatchMultiple: 1,
      purchaseDestination: 'AMBOS',
      trackInventory: true,
      isActive: true
    });
    setErrors({});

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-macartur-primary" />
            {ingredient ? 'Editar Insumo' : 'Nuevo Insumo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Información básica */}
          <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) setErrors({ ...errors, name: '' });
                  }}
                  placeholder="Ej: Pechuga de Pollo"
                  className={errors.name ? 'border-red-500' : ''}
                  required
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="category">Categoría *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value: IngredientCategory) =>
                    setFormData({ ...formData, category: value })
                  }
                  disabled={categoriesLoading}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categoriesLoading && (
                  <p className="text-sm text-gray-500 mt-1">Cargando categorías...</p>
                )}
              </div>

              <div>
                <Label htmlFor="unit">Unidad de Medida *</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value: IngredientUnit) =>
                    setFormData({ ...formData, unit: value })
                  }
                  disabled={unitsLoading}
                >
                  <SelectTrigger id="unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {unitsLoading && (
                  <p className="text-sm text-gray-500 mt-1">Cargando unidades...</p>
                )}
              </div>


              {/* Nuevos campos: Lista destino, Múltiplo despacho, Destino compra */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="listDestination">Lista Destino</Label>
                  <Select
                    value={formData.listDestination ?? 'AMBOS'}
                    onValueChange={(value: ListDestination) =>
                      setFormData({ ...formData, listDestination: value })
                    }
                  >
                    <SelectTrigger id="listDestination">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOCAL">Local</SelectItem>
                      <SelectItem value="PRINCIPAL">Local Principal</SelectItem>
                      <SelectItem value="AMBOS">Ambos</SelectItem>
                      <SelectItem value="NO">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="dispatchMultiple">Mult. Despacho</Label>
                  <Input
                    id="dispatchMultiple"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.dispatchMultiple ?? 0}
                    onChange={(e) =>
                      setFormData({ ...formData, dispatchMultiple: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="1"
                  />
                </div>

                <div>
                  <Label htmlFor="purchaseDestination">Destino Compra</Label>
                  <Select
                    value={formData.purchaseDestination ?? 'AMBOS'}
                    onValueChange={(value: ListDestination) =>
                      setFormData({ ...formData, purchaseDestination: value })
                    }
                  >
                    <SelectTrigger id="purchaseDestination">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOCAL">Local</SelectItem>
                      <SelectItem value="PRINCIPAL">Local Principal</SelectItem>
                      <SelectItem value="AMBOS">Ambos</SelectItem>
                      <SelectItem value="NO">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Checkbox: Se realiza inventario */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="trackInventory"
                  checked={formData.trackInventory ?? true}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, trackInventory: checked === true })
                  }
                />
                <Label htmlFor="trackInventory" className="cursor-pointer">
                  Se realiza inventario
                </Label>
              </div>

            </div>

          {/* Descripción */}
          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descripción del insumo..."
              rows={3}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.name?.trim()}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Guardando...' : (ingredient ? 'Actualizar' : 'Crear Insumo')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};